import {AnswerStatus, QuestionDifficulty} from '@domain/pom'
import {spawnSuccessToast} from '@hm/fundamental'
import CloseIcon from '@mui/icons-material/Close'
import {Box, IconButton, Paper, Typography} from '@mui/material'
import React, {FC, useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  DataFetchingPlaceholder,
  LevelCompletion,
  QuestionDetail,
  QuestionTable
} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {
  usePlantQuestions,
  useSubmitAnswer,
  usePlantUpmId,
  useStringSearchParameter,
  useQuestionState
} from '../hooks'
import {BackendQuestion, QuestionAnswer} from '../types'
import {
  ANSWER_STATUS_LABEL_COLOR_MAP,
  doesAnswerCompleteCategory,
  questionsToQuestionItemsMap
} from '../utils'

import {ActionCardSkeleton} from './ActionsCardContainer'

type QuestionListSideCardContainerProps = {
  difficulty: QuestionDifficulty
  customQuestionsFilterFunction?: (value: BackendQuestion) => boolean
}

const ANSWER_STATUS_TRANSLATION_KEY_MAP: Record<
  Exclude<AnswerStatus, AnswerStatus.Empty>,
  string
> = {
  [AnswerStatus.Positive]: 'answersStatistics.positive',
  [AnswerStatus.Negative]: 'answersStatistics.negative',
  [AnswerStatus.NotAvailable]: 'answersStatistics.notAvailable'
}

export const QuestionListSideCardContainer: FC<QuestionListSideCardContainerProps> = ({
  difficulty,
  customQuestionsFilterFunction
}) => {
  const plantId = usePlantUpmId()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {data, isLoading} = usePlantQuestions()
  const [selectedQuestionId, setQuestionId] = useStringSearchParameter('selectedQuestionId')
  const [{activeAnswerStatus, activeDifficulty}, {setActiveAnswerStatus}] = useQuestionState()

  const questions = useMemo(() => {
    const filteredData: BackendQuestion[] | undefined = customQuestionsFilterFunction
      ? data?.filter(customQuestionsFilterFunction)
      : data
    return questionsToQuestionItemsMap(filteredData ?? [])
      [difficulty].filter(({answer}) =>
        activeAnswerStatus === AnswerStatus.Empty
          ? !answer || answer === AnswerStatus.Empty
          : answer === activeAnswerStatus
      )
      .filter(({difficulty}) => difficulty === activeDifficulty)
  }, [activeAnswerStatus, activeDifficulty, customQuestionsFilterFunction, data, difficulty])

  const [showCompletionDialog, setShowCompletionDialog] = useState(false)
  const onSuccess = useCallback(
    (result: QuestionAnswer) => {
      spawnSuccessToast(t('questionnaire.questionSubmitted.success'))
      if (doesAnswerCompleteCategory(result, (data ?? []) as BackendQuestion[])) {
        setShowCompletionDialog(true)
      }
    },
    [data, t]
  )
  const {mutate: submitAnswer} = useSubmitAnswer(onSuccess)
  const onAnswerSelected = (id: string, answer: AnswerStatus) => {
    submitAnswer({
      plantId,
      questionId: id,
      answer
    })
  }

  const selectedQuestion = useMemo(
    () => questions.find(({id}) => id === selectedQuestionId),
    [questions, selectedQuestionId]
  )

  return isLoading ? (
    <DataFetchingPlaceholder
      isLoading={isLoading}
      height="100%"
      loader={<ActionCardSkeleton />}
      errorMessage={t('errors.questionsNotFetched')}
    />
  ) : (
    <Paper data-test-id="question-list-side-card" sx={{height: '100%', p: 2.5}}>
      {selectedQuestion ? (
        <QuestionDetail
          selectedQuestion={selectedQuestion}
          onClose={() => setQuestionId(undefined)}
          onSelected={onAnswerSelected}
        />
      ) : (
        <>
          <Box display="flex" mb={4} mt={1.5} alignItems="center" justifyContent="space-between">
            <Typography variant="h3">
              {activeAnswerStatus && activeAnswerStatus !== AnswerStatus.Empty ? (
                <>
                  {t('questionSideCars.label.allAnsweredWith')}{' '}
                  <Box component="span" color={ANSWER_STATUS_LABEL_COLOR_MAP[activeAnswerStatus]}>
                    {t(ANSWER_STATUS_TRANSLATION_KEY_MAP[activeAnswerStatus])}
                  </Box>
                </>
              ) : (
                t('questionSideCars.label.allUnansweredQuestions')
              )}{' '}
              ({questions.length})
            </Typography>
            <IconButton
              sx={{mr: 1.5, color: 'primary.main'}}
              onClick={() => setActiveAnswerStatus(undefined)}
              data-test-id="question-list-side-card-close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <QuestionTable
            setQuestionId={setQuestionId}
            questionItems={questions}
            onAnswerSelected={onAnswerSelected}
          />
        </>
      )}

      {showCompletionDialog && (
        <LevelCompletion
          open={showCompletionDialog}
          sectionCompleted={difficulty}
          onOpenChange={(open) => setShowCompletionDialog(open)}
        />
      )}
    </Paper>
  )
}
